import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Trophy } from 'lucide-react';

interface Match {
  id: string;
  team1_name?: string;
  team2_name?: string;
  team1_score?: number;
  team2_score?: number;
  status: string;
  bracket_type?: string;
}

interface StandingsTableProps {
  matches: Match[];
  showPools?: boolean;
}

interface TeamStanding {
  teamName: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
  pointsDiff: number;
  winPercentage: number;
  pool?: string;
}

const StandingsTable: React.FC<StandingsTableProps> = ({ matches, showPools = false }) => {
  const calculateStandings = (): TeamStanding[] => {
    const teamStats = new Map<string, TeamStanding>();

    // Initialize teams from matches
    matches.forEach(match => {
      if (match.team1_name && !teamStats.has(match.team1_name)) {
        teamStats.set(match.team1_name, {
          teamName: match.team1_name,
          wins: 0,
          losses: 0,
          ties: 0,
          pointsFor: 0,
          pointsAgainst: 0,
          pointsDiff: 0,
          winPercentage: 0,
          pool: showPools ? getPoolFromMatch(match) : undefined,
        });
      }
      
      if (match.team2_name && !teamStats.has(match.team2_name)) {
        teamStats.set(match.team2_name, {
          teamName: match.team2_name,
          wins: 0,
          losses: 0,
          ties: 0,
          pointsFor: 0,
          pointsAgainst: 0,
          pointsDiff: 0,
          winPercentage: 0,
          pool: showPools ? getPoolFromMatch(match) : undefined,
        });
      }
    });

    // Calculate stats from completed matches
    matches.forEach(match => {
      if (match.status === 'completed' && 
          match.team1_name && match.team2_name && 
          match.team1_score !== undefined && match.team2_score !== undefined) {
        
        const team1Stats = teamStats.get(match.team1_name)!;
        const team2Stats = teamStats.get(match.team2_name)!;

        // Update points
        team1Stats.pointsFor += match.team1_score;
        team1Stats.pointsAgainst += match.team2_score;
        team2Stats.pointsFor += match.team2_score;
        team2Stats.pointsAgainst += match.team1_score;

        // Update win/loss/tie
        if (match.team1_score > match.team2_score) {
          team1Stats.wins++;
          team2Stats.losses++;
        } else if (match.team2_score > match.team1_score) {
          team2Stats.wins++;
          team1Stats.losses++;
        } else {
          team1Stats.ties++;
          team2Stats.ties++;
        }
      }
    });

    // Calculate derived stats
    teamStats.forEach(stats => {
      stats.pointsDiff = stats.pointsFor - stats.pointsAgainst;
      const totalGames = stats.wins + stats.losses + stats.ties;
      stats.winPercentage = totalGames > 0 ? (stats.wins + stats.ties * 0.5) / totalGames : 0;
    });

    return Array.from(teamStats.values()).sort((a, b) => {
      // Sort by win percentage first, then by point differential
      if (b.winPercentage !== a.winPercentage) {
        return b.winPercentage - a.winPercentage;
      }
      return b.pointsDiff - a.pointsDiff;
    });
  };

  const getPoolFromMatch = (match: Match): string => {
    // Extract pool information from bracket_type or match data
    // This is a simplified implementation - you might need to adjust based on your data structure
    if (match.bracket_type?.includes('pool')) {
      return match.bracket_type.replace('pool', 'Pool ').toUpperCase();
    }
    return 'Pool A'; // Default fallback
  };

  const standings = calculateStandings();

  if (standings.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No Standings Available</h3>
        <p className="text-muted-foreground">
          Standings will be displayed once matches are completed.
        </p>
      </div>
    );
  }

  if (showPools) {
    // Group standings by pool
    const poolGroups = standings.reduce((groups, team) => {
      const pool = team.pool || 'Pool A';
      if (!groups[pool]) {
        groups[pool] = [];
      }
      groups[pool].push(team);
      return groups;
    }, {} as Record<string, TeamStanding[]>);

    return (
      <div className="space-y-6">
        {Object.entries(poolGroups).map(([poolName, poolTeams]) => (
          <div key={poolName}>
            <h3 className="text-lg font-semibold mb-3">{poolName}</h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead className="text-center">W</TableHead>
                    <TableHead className="text-center">L</TableHead>
                    <TableHead className="text-center">T</TableHead>
                    <TableHead className="text-center">Win %</TableHead>
                    <TableHead className="text-center">PF</TableHead>
                    <TableHead className="text-center">PA</TableHead>
                    <TableHead className="text-center">Diff</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poolTeams.map((team, index) => (
                    <TableRow key={team.teamName}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {index + 1}
                          {index === 0 && (
                            <Badge variant="default" className="text-xs">
                              1st
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{team.teamName}</TableCell>
                      <TableCell className="text-center">{team.wins}</TableCell>
                      <TableCell className="text-center">{team.losses}</TableCell>
                      <TableCell className="text-center">{team.ties}</TableCell>
                      <TableCell className="text-center">
                        {(team.winPercentage * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-center">{team.pointsFor}</TableCell>
                      <TableCell className="text-center">{team.pointsAgainst}</TableCell>
                      <TableCell className="text-center">
                        <span className={team.pointsDiff >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {team.pointsDiff > 0 ? '+' : ''}{team.pointsDiff}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="text-center">W</TableHead>
            <TableHead className="text-center">L</TableHead>
            <TableHead className="text-center">T</TableHead>
            <TableHead className="text-center">Win %</TableHead>
            <TableHead className="text-center">PF</TableHead>
            <TableHead className="text-center">PA</TableHead>
            <TableHead className="text-center">Diff</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((team, index) => (
            <TableRow key={team.teamName}>
              <TableCell>
                <div className="flex items-center gap-2">
                  {index + 1}
                  {index === 0 && (
                    <Trophy className="h-4 w-4 text-primary" />
                  )}
                  {index < 3 && (
                    <Badge variant={index === 0 ? "default" : "secondary"} className="text-xs">
                      {index === 0 ? '1st' : index === 1 ? '2nd' : '3rd'}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-medium">{team.teamName}</TableCell>
              <TableCell className="text-center">{team.wins}</TableCell>
              <TableCell className="text-center">{team.losses}</TableCell>
              <TableCell className="text-center">{team.ties}</TableCell>
              <TableCell className="text-center">
                {(team.winPercentage * 100).toFixed(1)}%
              </TableCell>
              <TableCell className="text-center">{team.pointsFor}</TableCell>
              <TableCell className="text-center">{team.pointsAgainst}</TableCell>
              <TableCell className="text-center">
                <span className={team.pointsDiff >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {team.pointsDiff > 0 ? '+' : ''}{team.pointsDiff}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default StandingsTable;